import { Link } from "react-router-dom";
import {
  Globe2, Lightbulb, Brain, UtensilsCrossed, Sprout, Link2, Plane, Trees,
  Users, Rocket, Telescope, Star, Compass, Sparkles, Feather, Quote, ArrowLeft,
} from "lucide-react";
import { PageHeader, vibrantTheme } from "../components/common";
import { SITE } from "../lib/site";

const PHOTOS = {
  portrait: "/founder-portrait.jpg",
  foodpulse: "/founder-foodpulse.webp",
  spice: "/founder-spicenflavor.jpg",
};

const EINSTEIN_QUOTE = "Imagination is more important than knowledge. Knowledge is limited. Imagination encircles the world.";

const Section = ({ icon: Icon, title, children, testid }) => (
  <section data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <span className="p-2 text-white" style={{ backgroundColor: vibrantTheme(title).bg }}><Icon className="w-5 h-5" /></span>
      <h2 className="font-heading font-900 text-xl sm:text-2xl tracking-tight">{title}</h2>
    </div>
    <div className="space-y-3 text-zinc-700 leading-relaxed text-base">{children}</div>
  </section>
);

const TagList = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((item) => (
      <span key={item} className="text-xs border border-zinc-200 bg-zinc-50 px-2 py-1 text-zinc-700">{item}</span>
    ))}
  </div>
);

const QuoteBlock = ({ children, attribution }) => (
  <blockquote className="border-l-4 border-[#F4C77B] bg-zinc-50 px-5 py-4 my-2">
    <Quote className="w-4 h-4 text-[#c99a3f] mb-2" />
    <p className="font-heading font-700 text-lg text-zinc-800 leading-snug">{children}</p>
    {attribution && <footer className="mt-2 text-sm text-muted-foreground">— {attribution}</footer>}
  </blockquote>
);

const PRINCIPLES = [
  ["Imagine First", "Don't allow today's limitations to define tomorrow's possibilities."],
  ["Learn Continuously", "Knowledge provides the tools necessary to turn ideas into reality."],
  ["Connect the Unconnected", "Some of the best ideas emerge when two unrelated concepts are brought together."],
  ["Build for People", "Technology has value when it makes people's lives better."],
  ["Experiment", "Ideas become meaningful only when someone is willing to try."],
  ["Learn From Failure", "A failed experiment can be valuable knowledge for the next attempt."],
  ["Think Beyond the Present", "Build things that may become more useful tomorrow than they are today."],
  ["Share Knowledge", "Ideas become more powerful when other people can build upon them."],
  ["Preserve What Matters", "People, memories, culture, food, nature and knowledge deserve to be documented."],
  ["Never Stop Imagining", "There is always another problem to solve, another connection to make and another possibility to explore."],
];

const MISSION_ITEMS = [
  "Imagine beyond conventional boundaries.",
  "Identify problems that affect everyday people.",
  "Connect ideas that are normally treated separately.",
  "Use technology to make solutions accessible.",
  "Create platforms that encourage participation rather than passive consumption.",
  "Preserve knowledge, memories and experiences.",
  "Reduce waste and encourage responsible use of resources.",
  "Connect people, communities and cultures.",
  "Learn continuously from experimentation.",
  "Leave behind useful ideas and tools that can continue evolving.",
];

export default function AboutFounder() {
  return (
    <div data-testid="about-founder-page">
      <PageHeader eyebrow="About the Founder" title="Tom (Thomas George) Vellaringattu" entityCode="FOUNDER"
        subtitle="Vision, Mission & Philosophy — Imagine What Could Be. Then Build It.">
        <div className="mt-5 font-mono text-sm tracking-[0.18em] text-[#F4C77B]" data-testid="founder-motto">
          Imagine • Learn • Connect • Build
        </div>
      </PageHeader>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12 space-y-14">
        {/* INTRO — plain portrait photo on top */}
        <section className="grid md:grid-cols-[280px,1fr] gap-8 items-start" data-testid="founder-intro">
          <img src={PHOTOS.portrait} alt="Tom (Thomas George) Vellaringattu"
            data-testid="founder-photo-portrait"
            className="w-56 md:w-full mx-auto border border-zinc-300 shadow-lg" />
          <div className="space-y-4 text-zinc-700 leading-relaxed">
            <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight">Imagine What Could Be. Then Build It.</h2>
            <p>
              Tom (Thomas) Vellaringattu believes that meaningful progress begins with a willingness to imagine
              something that does not yet exist. His guiding philosophy is inspired by Albert Einstein's words:
            </p>
            <QuoteBlock attribution="Albert Einstein">{EINSTEIN_QUOTE}</QuoteBlock>
            <p>
              For Tom, this does not mean that knowledge is unimportant. Quite the opposite: knowledge provides the
              foundation, but imagination provides the direction.
            </p>
            <ul className="space-y-1.5 text-base">
              <li className="flex gap-2"><span className="text-primary">•</span>Knowledge tells us what has been done. Imagination asks what could be done.</li>
              <li className="flex gap-2"><span className="text-primary">•</span>Knowledge helps us understand the world as it is. Imagination gives us the courage to envision the world as it could be.</li>
            </ul>
            <p>
              That distinction has become a central philosophy behind Tom's entrepreneurial, culinary, technological
              and community-oriented endeavors.
            </p>
          </div>
        </section>

        <Section icon={Globe2} title="His Vision" testid="founder-vision">
          <p className="font-700 text-zinc-900">
            To imagine, create and connect ideas that can make everyday life better for people, communities and the
            world around us.
          </p>
          <p>
            Tom's vision is not limited to one company, profession or industry. It is a belief that seemingly
            unrelated ideas can be connected to create something new.
          </p>
          <TagList items={[
            "Food can connect people.", "Technology can connect communities.", "Travel can connect cultures.",
            "Family history can connect generations.", "Nature can connect humanity to the planet.",
            "Information can empower people.", "And imagination can connect all of these possibilities.",
          ]} />
          <p>
            His long-term vision is therefore to continue creating useful platforms, businesses, services, content
            and communities that turn ideas into practical tools for people.
          </p>
          <div className="border border-zinc-300 bg-white p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Tom Vellaringattu — Vision in One Sentence</div>
            <p className="font-heading font-700 text-lg text-zinc-900 leading-snug">
              To imagine a better possibility, connect people and ideas, and turn those possibilities into practical
              creations that make life more connected, informed, sustainable and meaningful.
            </p>
          </div>
        </Section>

        <Section icon={Lightbulb} title="The Philosophy: Imagination Before Limitation" testid="founder-philosophy">
          <p>Tom's interpretation of Einstein's philosophy is simple:</p>
          <p>Don't begin with <span className="italic">"Can this be done?"</span> Begin with:</p>
          <p className="font-heading font-700 text-xl text-zinc-900">"What if it could be done?"</p>
          <p>Only after imagining the possibility should we ask:</p>
          <TagList items={[
            "What knowledge do we need?", "What technology is available?", "What resources are required?",
            "What problems must be solved?", "Who can help?", "How can the idea become practical?",
          ]} />
          <p>
            This philosophy allows imagination to become the starting point, while knowledge, experience,
            experimentation and persistence become the tools for turning imagination into reality.
          </p>
        </Section>

        <Section icon={Brain} title="Knowledge Is the Foundation. Imagination Is the Horizon." testid="founder-knowledge">
          <p>Tom does not see imagination and knowledge as competitors. He sees them as partners.</p>
          <p>
            Knowledge without imagination can keep us within the boundaries of what is already known. Imagination
            without knowledge can remain only an idea. But:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-zinc-300 bg-white p-5 text-center font-mono text-sm font-700 text-zinc-900">
              Imagination + Knowledge + Persistence = Possibility
            </div>
            <div className="border border-zinc-300 bg-white p-5 text-center font-mono text-sm font-700 text-zinc-900">
              Possibility + Action = Creation
            </div>
          </div>
          <p>
            This is the philosophy Tom seeks to apply to his current endeavors and to everything he may undertake in
            the future.
          </p>
        </Section>

        <Section icon={UtensilsCrossed} title="From Food to Ideas" testid="founder-food-to-ideas">
          <p>Tom's entrepreneurial journey illustrates this philosophy.</p>
          <p>
            Spice n Flavor began in 2000 as an Indian spice-blend enterprise in New York, introducing multiple spice
            blends under the brand India's Spice n Flavor. Later, he opened the Spice n Flavor restaurant in San Jose
            and pursued food delivery and culinary ventures.
          </p>
          <p>These experiences demonstrate an important part of his philosophy: an idea doesn't have to remain in one form.</p>
          <TagList items={[
            "A spice blend can become a brand.", "A restaurant can become a community.",
            "A chef can become an entrepreneur.",
            "A food business can become a platform for addressing food waste, food access and community participation.",
            "And a challenge can become the starting point for the next idea.",
          ]} />
        </Section>

        {/* FOODPULSE photo — middle of the article */}
        <section className="grid md:grid-cols-[260px,1fr] gap-8 items-start" data-testid="founder-foodpulse-section">
          <img src={PHOTOS.foodpulse} alt="Tom Vellaringattu — Food Pulse, Nourishing Communities"
            data-testid="founder-photo-foodpulse"
            className="w-52 md:w-full mx-auto" />
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="p-2 text-white" style={{ backgroundColor: vibrantTheme("Food, Community & Food Waste").bg }}><Sprout className="w-5 h-5" /></span>
              <h2 className="font-heading font-900 text-xl sm:text-2xl tracking-tight">Food, Community &amp; Food Waste</h2>
            </div>
            <div className="space-y-3 text-zinc-700 leading-relaxed text-base">
              <p>Tom's vision extends beyond preparing and selling food. He sees food as one of the greatest opportunities to connect:</p>
              <p className="font-mono text-sm font-700 text-zinc-900">
                Farmers → Producers → Restaurants → Stores → Families → Communities → Charities
              </p>
              <p>The goal is not simply to ask <span className="italic">"What food can we sell?"</span> but also:</p>
              <TagList items={[
                '"What food can we save?"', '"What food can we share?"',
                '"How can technology reduce waste?"', '"How can communities help one another?"',
              ]} />
              <p>
                This way of thinking is central to the broader FoodPulse concept: using technology, information and
                community participation to help people discover food, save money, share surplus and reduce waste.
              </p>
            </div>
          </div>
        </section>

        <Section icon={Link2} title="Building Connections" testid="founder-connections">
          <p>A recurring theme in Tom's ideas is connection. Connecting:</p>
          <TagList items={[
            "People with people", "Families with their history", "Students with classmates and teachers",
            "Travelers with airlines and airports", "Consumers with food information",
            "Communities with surplus food", "People with nature", "Ideas with technology",
          ]} />
          <p>
            This is why his projects can appear diverse while sharing a common foundation. The subject changes.
            The philosophy does not.
          </p>
        </Section>

        <Section icon={Plane} title="Travel & Exploration" testid="founder-travel">
          <p>Travel represents another expression of imagination. Every journey begins with an imagined destination:</p>
          <p className="font-heading font-700 text-lg text-zinc-900">"What would it be like to go there?"</p>
          <p>
            Tom's vision for <Link to="/about" className="text-primary font-700 hover:underline">TravelerPulse</Link> and
            travel-related projects is to make that journey more informed and organized—helping travelers research
            airlines, airports, destinations, travel requirements, packing, safety, transportation and their own
            travel history.
          </p>
          <p>
            The objective isn't simply to provide information. It is to help people imagine, plan, experience and
            remember their journeys.
          </p>
        </Section>

        <Section icon={Trees} title="Nature & the Future" testid="founder-nature">
          <p>
            Tom's interest in nature and environmental information reflects another dimension of his philosophy.
            A future in which people can document:
          </p>
          <TagList items={[
            "Plants", "Animals", "Birds", "Fish", "Insects", "Landscapes", "Rivers", "Lakes",
            "Weather", "Global Warming", "Rocks", "Ecosystems",
          ]} />
          <p>can transform scattered observations into a collective understanding of our planet. The idea is simple:</p>
          <p className="font-heading font-700 text-lg text-zinc-900">
            If we imagine a living digital museum of the natural world, what could humanity learn from it?
          </p>
          <p>
            That question can lead to something much larger than a database. It can become a way of encouraging
            people to observe, document, appreciate and protect nature.
          </p>
        </Section>

        <Section icon={Users} title="Preserving People & Memories" testid="founder-memories">
          <p>
            Another expression of this philosophy is the idea that technology should preserve things that might
            otherwise disappear.
          </p>
          <TagList items={[
            "Family histories", "School memories", "Friendships", "Photographs", "Stories",
            "Recipes", "Places", "Journeys", "Experiences",
          ]} />
          <p>
            Tom's projects involving family and alumni connections reflect the belief that technology should not
            only create the future—it should preserve the past.
          </p>
        </Section>

        <Section icon={Rocket} title="Entrepreneurship as an Experiment" testid="founder-entrepreneurship">
          <p>Tom sees entrepreneurship less as a straight road and more as a continuous experiment. An idea may:</p>
          <p className="font-mono text-sm font-700 text-zinc-900">
            Work → Grow → Change → Fail → Teach → Reappear in another form
          </p>
          <p>A discontinued project is not necessarily a failure. It can become: knowledge for the next idea.</p>
          <TagList items={[
            "Every experiment provides information.", "Every obstacle reveals a constraint.",
            "Every constraint can inspire another solution.", "Every solution can create another possibility.",
          ]} />
        </Section>

        <Section icon={Telescope} title="His Mission" testid="founder-mission">
          <p className="font-700 text-zinc-900">Tom's mission is to transform imagination into useful reality. He seeks to:</p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-base">
            {MISSION_ITEMS.map((m) => <li key={m} className="flex gap-2"><span className="text-primary">•</span>{m}</li>)}
          </ul>
        </Section>

        <Section icon={Star} title="A Philosophy for Every Future Endeavor" testid="founder-future">
          <p>Tom's future projects do not have to belong to a particular industry. They could involve:</p>
          <TagList items={[
            "Food", "Technology", "Travel", "Nature", "Education", "Family", "Community",
            "Information", "Entrepreneurship", "Artificial intelligence", "Environmental sustainability",
          ]} />
          <p>The common thread is the question:</p>
          <p className="font-heading font-700 text-lg text-zinc-900">"What if we could make this better?"</p>
          <p>That question is often more important than knowing the answer beforehand.</p>
        </Section>

        {/* PERSONAL PRINCIPLES — spicenflavor photo towards the bottom */}
        <section className="grid md:grid-cols-[1fr,260px] gap-8 items-start" data-testid="founder-principles-section">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="p-2 text-white" style={{ backgroundColor: vibrantTheme("Tom's Personal Principles").bg }}><Compass className="w-5 h-5" /></span>
              <h2 className="font-heading font-900 text-xl sm:text-2xl tracking-tight">Tom's Personal Principles</h2>
            </div>
            <ol className="space-y-3">
              {PRINCIPLES.map(([title, body], i) => (
                <li key={title} className="border border-zinc-300 bg-white p-4 flex gap-4">
                  <span className="font-mono font-700 text-primary shrink-0 w-6 text-right">{i + 1}.</span>
                  <div>
                    <div className="font-heading font-700 text-base text-zinc-900">{title}</div>
                    <p className="text-sm text-zinc-700 mt-0.5">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <img src={PHOTOS.spice} alt="Tom Vellaringattu — Spice n Flavor, Flavorful India"
            data-testid="founder-photo-spicenflavor"
            className="w-52 md:w-full mx-auto border border-zinc-300 shadow-lg md:sticky md:top-24" />
        </section>

        <Section icon={Sparkles} title="His Broader Purpose" testid="founder-purpose">
          <p>At the heart of Tom's vision is a simple belief:</p>
          <p className="font-heading font-700 text-lg text-zinc-900">
            The world does not need more information alone. It needs people who can imagine what to do with that
            information.
          </p>
          <p>
            The internet has made enormous amounts of knowledge accessible. Artificial intelligence is making
            information even easier to obtain. But information by itself does not create progress.
          </p>
          <TagList items={[
            "Someone must imagine a new possibility.", "Someone must ask a different question.",
            "Someone must connect two ideas.", "Someone must attempt something that hasn't been attempted before.",
            "And someone must be willing to turn that idea into reality.",
          ]} />
          <p>That is where Tom sees his role.</p>
        </Section>

        <Section icon={Feather} title="A Personal Vision Statement" testid="founder-statement">
          <QuoteBlock>
            I want to spend my life imagining possibilities, learning what is necessary to make them real, and
            creating things that can help people, communities and the world.
          </QuoteBlock>
          <p>
            I don't want to be limited by what already exists. I want to ask what could exist. I believe knowledge
            gives us the foundation, but imagination gives us the courage to build beyond it.
          </p>
          <p>
            Every idea begins as an imagination. Every creation begins as a possibility. Every meaningful change
            begins when someone decides to try.
          </p>
        </Section>

        {/* GUIDING THOUGHT — closing */}
        <section className="text-white p-8 sm:p-12" style={{ backgroundColor: vibrantTheme("guiding-thought").bg }} data-testid="founder-guiding-thought">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight mb-5">Tom's Guiding Thought</h2>
          <p className="font-heading font-700 text-xl text-[#F4C77B] mb-6">
            "Imagine beyond what you know. Learn what you need. Build what you imagine."
          </p>
          <blockquote className="border-l-4 border-white/40 pl-5">
            <p className="text-white/95 leading-relaxed italic">{EINSTEIN_QUOTE}</p>
            <footer className="mt-2 text-sm text-white/70">— Albert Einstein</footer>
          </blockquote>
          <p className="text-white/85 leading-relaxed mt-6 text-sm">
            This is especially fitting because Einstein's original context was not a rejection of knowledge; he was
            describing the role of imagination and intuition in discovery. The fuller version says imagination can
            stimulate progress and function as a factor in scientific research.
          </p>
          <div className="border-t border-white/20 pt-6 mt-8 text-center">
            <div className="font-heading font-900 text-xl">Tom (Thomas George) Vellaringattu</div>
            <div className="text-sm text-white/80 mt-1">Founder — {SITE.brandLabel}</div>
            <Link to="/about" data-testid="founder-back-to-about"
              className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-white text-zinc-900 text-sm font-700 hover:bg-[#F4C77B] transition-colors">
              <ArrowLeft className="w-4 h-4" /> About {SITE.brandLabel}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
